import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { SELECTION_CHANGE_COMMAND } from 'lexical';
import { ComponentType, useCallback, useEffect, useState } from 'react';

import {
  ButtonWithPopover,
  ButtonWithPopoverSize,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { ButtonVariant } from '@/components/form/baseInputs/Button/types';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { CheckSquareIcon } from '@/components/icons/CheckSquareIcon';
import {
  IconProps,
  Rows03FilledIcon,
} from '@/components/icons/Rows03FilledIcon';
import { SquareIcon } from '@/components/icons/SquareIcon';
import { TableIcon } from '@/components/icons/TableIcon';
import { Trash03Icon } from '@/components/icons/Trash03Icon';
import { XSquareIcon } from '@/components/icons/XSquareIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';

import {
  deleteColumn,
  deleteRow,
  deleteTable,
  hasHeaderRow,
  insertColumnLeft,
  insertColumnRight,
  insertRowAbove,
  insertRowBelow,
  isSelectionInTable,
  toggleHeaderRow,
} from './tableUtils';

const LowPriority = 1;

const RotatedIcon = ({
  Icon,
  orientation,
}: {
  Icon: ComponentType<IconProps>;
  orientation: '90deg' | '180deg' | '270deg';
}) => {
  return <Icon sx={{ transform: `rotate(${orientation})` }} size={16} />;
};

interface TableActionToolbarProps {
  size?: ButtonWithPopoverSize;
  variant?: ButtonVariant;
  square?: boolean;
}

export function TableActionToolbar({
  size = 'sm',
  variant = 'secondary',
  square,
}: TableActionToolbarProps = {}) {
  const [editor] = useLexicalComposerContext();
  const [isInTable, setIsInTable] = useState(false);
  const [showHeaderRow, setShowHeaderRow] = useState(false);

  const updateTableState = useCallback(() => {
    editor.getEditorState().read(() => {
      setIsInTable(isSelectionInTable());
      setShowHeaderRow(hasHeaderRow());
    });
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(() => {
        updateTableState();
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateTableState();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateTableState]);

  // Only show the toolbar if the selection is in a table.
  if (!isInTable) {
    return null;
  }

  return (
    <ButtonWithPopover
      size={size}
      variant={variant}
      square={square}
      label={<TableIcon size={16} />}
      popperVariant="menuBelow"
    >
      <SelectItemGroupLabel label="Rows" />
      <MenuItem
        icon={<Rows03FilledIcon size={16} />}
        label="Add row above"
        onClick={() => insertRowAbove(editor)}
      />
      <MenuItem
        icon={<RotatedIcon Icon={Rows03FilledIcon} orientation="180deg" />}
        label="Add row below"
        onClick={() => insertRowBelow(editor)}
      />
      <MenuItem
        variant="destructive"
        icon={<XSquareIcon size={16} />}
        label="Delete selected row"
        onClick={() => deleteRow(editor)}
      />

      <SelectItemGroupLabel label="Columns" />
      <MenuItem
        icon={<RotatedIcon Icon={Rows03FilledIcon} orientation="270deg" />}
        label="Add column before"
        onClick={() => insertColumnLeft(editor)}
      />
      <MenuItem
        icon={<RotatedIcon Icon={Rows03FilledIcon} orientation="90deg" />}
        label="Add column after"
        onClick={() => insertColumnRight(editor)}
      />
      <MenuItem
        variant="destructive"
        icon={<XSquareIcon size={16} />}
        label="Delete selected column"
        onClick={() => deleteColumn(editor)}
        muiMenuItemProps={{ divider: true }}
      />

      <MenuItem
        icon={
          showHeaderRow ? (
            <CheckSquareIcon size={16} />
          ) : (
            <SquareIcon size={16} />
          )
        }
        label="Show header row"
        onClick={() => toggleHeaderRow(editor)}
      />
      <MenuItem
        variant="destructive"
        icon={<Trash03Icon size={16} />}
        label="Delete table"
        onClick={() => deleteTable(editor)}
      />
    </ButtonWithPopover>
  );
}
